import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Code, 
  Book, 
  BarChart3, 
  Settings, 
  HelpCircle,
  ExternalLink,
  Menu,
  X,
  User,
  LogOut,
  ArrowLeft,
  Home,
  Rocket
} from 'lucide-react';

interface DeveloperAccount {
  id: number;
  status: string;
}

const baseNavItems = [
  {
    href: '/developer/dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    requiresDeveloper: true,
  },
  {
    href: '/developer-portal',
    label: 'Overview',
    icon: Code,
    requiresDeveloper: false,
  },
  {
    href: '/api-docs',
    label: 'Documentation',
    icon: Book,
    requiresDeveloper: false,
  },
  {
    href: '/developer-signup',
    label: 'Get Started',
    icon: Rocket,
    requiresDeveloper: false,
    hideIfDeveloper: true,
  },
];

const mainSiteItems = [
  { href: "/home", label: "Create Stories", icon: Book },
  { href: "/stories", label: "My Stories", icon: Book },
  { href: "/community", label: "Community", icon: User },
  { href: "/programs", label: "Programs", icon: Code },
];

export function DeveloperNav() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logoutMutation } = useAuth();

  const { data: developerAccountData } = useQuery<{ success: boolean; data: DeveloperAccount }>({
    queryKey: ['/api/developer/account'],
    enabled: isAuthenticated,
  });
  const hasDeveloperAccount = !!developerAccountData?.data;

  const navItems = baseNavItems.filter(item => {
    if (item.hideIfDeveloper && hasDeveloperAccount) return false;
    if (item.requiresDeveloper && !hasDeveloperAccount) return false;
    return true;
  });

  const getUserInitials = () => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link href="/" className="flex items-center">
              <Code className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-2" />
              <span className="text-lg sm:text-xl font-bold text-gray-900">SnuggleCrafters API</span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {/* Main Site Quick Access */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center">
                    <Home className="w-4 h-4 mr-2" />
                    Main Site
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 bg-white border border-gray-200 shadow-lg" align="start">
                  {mainSiteItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link href={item.href} className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded">
                          <Icon className="mr-2 h-4 w-4" />
                          <span>{item.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Developer Navigation */}
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location === item.href;
                
                return (
                  <Button
                    key={item.href}
                    asChild
                    variant={isActive ? "default" : "ghost"}
                    className={`flex items-center ${
                      isActive 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Link href={item.href}>
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button asChild variant="outline" size="sm" data-testid="button-support">
              <a href="mailto:developers@snugglecrafters.com">
                <HelpCircle className="w-4 h-4 mr-2" />
                Support
              </a>
            </Button>
            
            {isAuthenticated ? (
              // User Menu for authenticated users
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full" aria-label="Developer user menu">
                    <Avatar className="h-8 w-8">
                      {user?.profilePictureUrl && (
                        <AvatarImage 
                          src={user.profilePictureUrl} 
                          alt={`${user.firstName || 'User'}'s profile picture`}
                        />
                      )}
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {getUserInitials() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-white border border-gray-200 shadow-lg" align="end" forceMount>
                  <div className="flex flex-col space-y-1 p-2 border-b border-gray-100">
                    <p className="text-sm font-medium leading-none text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs leading-none text-gray-500">
                      {user?.email}
                    </p>
                  </div>
                  <div className="p-1">
                    <DropdownMenuItem asChild>
                      <Link href="/developer/dashboard" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        <span>Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/profile" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                        <User className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/home" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded" style={{ color: "#000000" }}>
                        <Book className="mr-2 h-4 w-4" />
                        <span>Main App</span>
                      </Link>
                    </DropdownMenuItem>
                  </div>
                  <div className="border-t border-gray-100 p-1">
                    <DropdownMenuItem
                      className="flex items-center cursor-pointer px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                      onClick={() => logoutMutation.mutate()}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Get API Key button for unauthenticated users
              <Button asChild size="sm" data-testid="button-get-api-key">
                <a href="/developer-signup">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Get API Key
                </a>
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Main Site Links */}
              <div className="border-b border-gray-100 pb-2 mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-1">Main Site</p>
                {mainSiteItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.href}
                      asChild
                      variant="ghost"
                      className="w-full justify-start text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link href={item.href}>
                        <Icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </Link>
                    </Button>
                  );
                })}
              </div>

              {/* Developer Section */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-1">Developer</p>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location === item.href;
                  
                  return (
                    <Button
                      key={item.href}
                      asChild
                      variant={isActive ? "default" : "ghost"}
                      className={`w-full justify-start ${
                        isActive 
                          ? 'bg-blue-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Link href={item.href}>
                        <Icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </Link>
                    </Button>
                  );
                })}
              </div>
              
              {/* Mobile Actions */}
              <div className="pt-4 space-y-2">
                <Button asChild variant="outline" className="w-full justify-start" data-testid="mobile-button-support">
                  <a href="mailto:developers@snugglecrafters.com">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Support
                  </a>
                </Button>
                
                {isAuthenticated ? (
                  // Authenticated user actions
                  <>
                    <div className="pt-2 pb-2 border-t border-gray-200">
                      <div className="flex items-center px-3 py-2">
                        <Avatar className="h-8 w-8 mr-3">
                          {user?.profilePictureUrl && (
                            <AvatarImage 
                              src={user.profilePictureUrl} 
                              alt={`${user.firstName || 'User'}'s profile picture`}
                            />
                          )}
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            {getUserInitials() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button asChild variant="ghost" className="w-full justify-start" data-testid="mobile-button-dashboard">
                      <Link href="/developer/dashboard">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" className="w-full justify-start" data-testid="mobile-button-profile">
                      <Link href="/profile">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Link>
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-red-600 hover:bg-red-50" 
                      onClick={() => logoutMutation.mutate()}
                      data-testid="mobile-button-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </Button>
                  </>
                ) : (
                  // Unauthenticated user actions
                  <Button asChild className="w-full justify-start" data-testid="mobile-button-get-api-key">
                    <a href="/developer-signup">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Get API Key
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}